import * as React from 'react';
import px from 'prop-types';
import cx from 'classnames';
import debounce from 'lodash.debounce';
import { createPortal } from 'react-dom';
import { getProduct } from 'Common/features/catalog/api';
import { useTranslation, useViewport } from 'Common/hooks';
import ProductPartialView from './ProductPartialView';

export default function ProductLinkQuickview({ code, linkText }) {
    const viewport = useViewport();
    const [showQuick, setShowQuick] = React.useState(false);
    const [productData, setProductData] = React.useState(null);

    const [position, setPosition] = React.useState({ x: null, y: null });

    const linkRef = React.useRef(null);
    const popupRef = React.useRef(null);
    const repositionRef = React.useRef(null);

    const lblClose = useTranslation('Commerce.CatalogNode.QuickView.Close');

    const fetchProduct = React.useCallback(async (c) => {
        const response = await getProduct(c);

        if (response?.success) {
            setProductData({ Product: response.products[0] });
        }
    }, []);

    // Build the debounced reposition function **after render** (safe to access refs)
    React.useEffect(() => {
        repositionRef.current = debounce(() => {
            const el = linkRef.current;
            const popup = popupRef.current;
            
            if (!el || !popup) return;

            const elRect = el.getBoundingClientRect();
            const bodyRect = document.body.getBoundingClientRect();
            const popupRect = popup.getBoundingClientRect();

            // vertical placement
            const y =
                elRect.top < window.innerHeight / 2
                    ? elRect.bottom - bodyRect.top
                    : elRect.bottom - bodyRect.top - popupRect.height - elRect.height;

            // horizontal placement
            const isFullScreen = viewport.is.lt('lg');
            const alignRight = window.innerWidth - elRect.left < popupRect.width / 2;
            const alignLeft = elRect.left < popupRect.width / 2;

            const x =
                isFullScreen ? 0 :
                alignRight ? window.innerWidth - popupRect.width :
                alignLeft ? elRect.left :
                elRect.left - popupRect.width / 2;

            setPosition({ x, y });
        }, 50);

        return () => {
            repositionRef.current?.cancel?.();
        };
    }, [viewport]); // Only rebuild when breakpoints change

    const onShowPopup = React.useCallback(() => {
        setShowQuick(true);
    }, []);

    // When popup becomes visible, reposition on next frame
    React.useEffect(() => {
        if (showQuick) {
            requestAnimationFrame(() => repositionRef.current?.());
        }
    }, [showQuick]);

    React.useEffect(() => {
        fetchProduct(code);
    }, [code, fetchProduct]);

    return (
        <span className="ProductLinkQuickview">
            <a ref={linkRef} className="wysiwyg-link" onClick={onShowPopup}>
                {linkText}
            </a>

            {productData &&
                createPortal(
                    <div
                        ref={popupRef}
                        className={cx('ProductViewPopup popup', showQuick ? 'show' : 'hide')}
                        style={{
                            left: position.x != null ? `${position.x}px` : undefined,
                            top: position.y != null ? `${position.y}px` : undefined,
                        }}
                    >
                        <button className="close-btn btn" onClick={() => setShowQuick(false)} aria-label={lblClose}>
                            <i className="fas fa-times-circle" />
                        </button>

                        <ProductPartialView isUserAuthenticated={true} productData={productData} />
                    </div>,
                    document.body
                )}
        </span>
    );
}

ProductLinkQuickview.propTypes = {
    code: px.string,
    linkText: px.string,
};
